/**
 * Impact Statistics Service
 * 
 * CRUD operations for the Community Impact statistics section on the homepage.
 */

import { safeFrom, isSupabaseAvailable } from '@/lib/safe-supabase';
import { 
  ServiceResponse, 
  successResponse, 
  errorResponse, 
  handleServiceError
} from '@/lib/service-errors';
import { auditContent } from '@/lib/audit-logger';

export type ImpactStat = {
  id: number;
  number: string;
  label: string;
  description: string | null;
  order_index: number;
  published: boolean;
  created_at: string;
  updated_at: string;
};

export type ImpactStatInput = {
  number: string;
  label: string;
  description?: string;
  order_index?: number;
  published?: boolean;
};

/**
 * Fetch all published stats (for public display)
 */
export const fetchPublishedStats = async (): Promise<ServiceResponse<ImpactStat[]>> => {
  if (!isSupabaseAvailable()) {
    return successResponse([]);
  }

  try {
    const { data, error } = await safeFrom('impact_stats')
      .select('*')
      .eq('published', true)
      .order('order_index', { ascending: true });

    if (error) {
      // If table doesn't exist (404), return empty array instead of error
      const errorMessage = error.message?.toLowerCase() || '';
      const errorCode = (error as any).code || (error as any).status || (error as any).statusCode;
      
      if (
        errorCode === 'PGRST116' || 
        errorCode === 404 || 
        errorCode === '404' ||
        errorMessage.includes('does not exist') || 
        errorMessage.includes('relation') ||
        errorMessage.includes('not found') ||
        errorMessage.includes('404')
      ) {
        return successResponse([]);
      }
      throw error;
    }

    return successResponse((data || []) as ImpactStat[]);
  } catch (error) {
    // Handle table not existing gracefully
    const errorMessage = error instanceof Error ? error.message.toLowerCase() : String(error).toLowerCase();
    const errorCode = (error as any)?.code || (error as any)?.status || (error as any)?.statusCode;
    
    if (
      errorCode === 404 || 
      errorCode === '404' ||
      errorMessage.includes('does not exist') || 
      errorMessage.includes('relation') ||
      errorMessage.includes('not found') ||
      errorMessage.includes('404')
    ) {
      return successResponse([]);
    }
    return errorResponse(handleServiceError(error, 'impactStatsService.fetchPublishedStats'));
  }
};

/**
 * Fetch all stats (for admin)
 */
export const fetchAllStats = async (): Promise<ServiceResponse<ImpactStat[]>> => {
  if (!isSupabaseAvailable()) {
    return successResponse([]);
  }

  try {
    const { data, error } = await safeFrom('impact_stats')
      .select('*')
      .order('order_index', { ascending: true });

    if (error) {
      // If table doesn't exist (404), return empty array instead of error
      const errorMessage = (error.message || String(error) || '').toLowerCase();
      const errorCode = (error as any).code || (error as any).status || (error as any).statusCode || (error as any).hint;
      const errorDetails = JSON.stringify(error).toLowerCase();
      
      // Check for various 404 indicators
      if (
        errorCode === 'PGRST116' || 
        errorCode === 404 || 
        errorCode === '404' ||
        errorCode === '42P01' || // PostgreSQL "relation does not exist"
        errorMessage.includes('does not exist') || 
        errorMessage.includes('relation') ||
        errorMessage.includes('not found') ||
        errorMessage.includes('404') ||
        errorDetails.includes('404') ||
        errorDetails.includes('does not exist') ||
        errorDetails.includes('relation')
      ) {
        // Table doesn't exist - return empty array silently
        return successResponse([]);
      }
      throw error;
    }

    return successResponse((data || []) as ImpactStat[]);
  } catch (error) {
    // Handle table not existing gracefully
    const errorMessage = error instanceof Error ? error.message.toLowerCase() : String(error).toLowerCase();
    const errorCode = (error as any)?.code || (error as any)?.status || (error as any)?.statusCode;
    const errorDetails = JSON.stringify(error).toLowerCase();
    
    if (
      errorCode === 404 || 
      errorCode === '404' ||
      errorCode === '42P01' ||
      errorMessage.includes('does not exist') || 
      errorMessage.includes('relation') ||
      errorMessage.includes('not found') ||
      errorMessage.includes('404') ||
      errorDetails.includes('404') ||
      errorDetails.includes('does not exist')
    ) {
      // Table doesn't exist - return empty array silently
      return successResponse([]);
    }
    return errorResponse(handleServiceError(error, 'impactStatsService.fetchAllStats'));
  }
};

/**
 * Create a new stat
 */
export const createStat = async (stat: ImpactStatInput): Promise<ServiceResponse<ImpactStat>> => {
  if (!isSupabaseAvailable()) {
    return errorResponse(handleServiceError(new Error('Supabase not configured'), 'impactStatsService.createStat'));
  }

  try {
    // Check for duplicates before creating
    const existingResult = await fetchAllStats();
    if (existingResult.success) {
      const duplicate = existingResult.data.find(
        s => s.number === stat.number && s.label === stat.label
      );
      if (duplicate) {
        return errorResponse(handleServiceError(
          new Error(`A statistic with number "${stat.number}" and label "${stat.label}" already exists.`),
          'impactStatsService.createStat'
        ));
      }
    }

    const { data, error } = await safeFrom('impact_stats')
      .insert([stat])
      .select()
      .single();

    if (error) {
      // Check if table doesn't exist (404)
      const errorMessage = (error.message || String(error) || '').toLowerCase();
      const errorCode = (error as any).code || (error as any).status || (error as any).statusCode || (error as any).hint;
      const errorDetails = JSON.stringify(error).toLowerCase();
      
      if (
        errorCode === 'PGRST116' || 
        errorCode === 404 || 
        errorCode === '404' ||
        errorCode === '42P01' || // PostgreSQL "relation does not exist"
        errorMessage.includes('does not exist') || 
        errorMessage.includes('relation') ||
        errorMessage.includes('not found') ||
        errorMessage.includes('404') ||
        errorDetails.includes('404') ||
        errorDetails.includes('does not exist') ||
        errorDetails.includes('relation')
      ) {
        return errorResponse(handleServiceError(
          new Error('The impact_stats table does not exist. Please run the database migration: 20260101000000_homepage_sections.sql'),
          'impactStatsService.createStat'
        ));
      }
      throw error;
    }

    const created = data as ImpactStat;
    
    // Audit log
    await auditContent.create('impact_stat', created.id.toString(), {
      label: created.label,
    });

    return successResponse(created);
  } catch (error) {
    // Handle table not existing gracefully
    const errorMessage = error instanceof Error ? error.message.toLowerCase() : String(error).toLowerCase();
    const errorCode = (error as any)?.code || (error as any)?.status || (error as any)?.statusCode;
    const errorDetails = JSON.stringify(error).toLowerCase();
    
    if (
      errorCode === 404 || 
      errorCode === '404' ||
      errorCode === '42P01' ||
      errorMessage.includes('does not exist') || 
      errorMessage.includes('relation') ||
      errorMessage.includes('not found') ||
      errorMessage.includes('404') ||
      errorDetails.includes('404') ||
      errorDetails.includes('does not exist')
    ) {
      return errorResponse(handleServiceError(
        new Error('The impact_stats table does not exist. Please run the database migration: 20260101000000_homepage_sections.sql'),
        'impactStatsService.createStat'
      ));
    }
    return errorResponse(handleServiceError(error, 'impactStatsService.createStat'));
  }
};

/**
 * Update a stat
 */
export const updateStat = async (id: number, stat: Partial<ImpactStatInput>): Promise<ServiceResponse<ImpactStat>> => {
  if (!isSupabaseAvailable()) {
    return errorResponse(handleServiceError(new Error('Supabase not configured'), 'impactStatsService.updateStat'));
  }

  try {
    // Check for duplicates before updating (excluding current stat)
    if (stat.number !== undefined || stat.label !== undefined) {
      const existingResult = await fetchAllStats();
      if (existingResult.success) {
        const currentStat = existingResult.data.find(s => s.id === id);
        const number = stat.number ?? currentStat?.number;
        const label = stat.label ?? currentStat?.label;
        
        const duplicate = existingResult.data.find(
          s => s.id !== id && s.number === number && s.label === label
        );
        if (duplicate) {
          return errorResponse(handleServiceError(
            new Error(`A statistic with number "${number}" and label "${label}" already exists.`),
            'impactStatsService.updateStat'
          ));
        }
      }
    }

    const { data, error } = await safeFrom('impact_stats')
      .update(stat)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      throw error;
    }

    const updated = data as ImpactStat;
    
    // Audit log
    await auditContent.update('impact_stat', id.toString(), {}, {
      label: updated.label,
    });

    return successResponse(updated);
  } catch (error) {
    return errorResponse(handleServiceError(error, 'impactStatsService.updateStat', { id }));
  }
};

/**
 * Delete a stat
 */
export const deleteStat = async (id: number): Promise<ServiceResponse<boolean>> => {
  if (!isSupabaseAvailable()) {
    return errorResponse(handleServiceError(new Error('Supabase not configured'), 'impactStatsService.deleteStat'));
  }

  try {
    const { error } = await safeFrom('impact_stats')
      .delete()
      .eq('id', id);

    if (error) {
      throw error;
    }

    // Audit log
    await auditContent.delete('impact_stat', id.toString(), { id });

    return successResponse(true);
  } catch (error) {
    return errorResponse(handleServiceError(error, 'impactStatsService.deleteStat', { id }));
  }
};

/**
 * Reorder stats
 */
export const reorderStats = async (stats: { id: number; order_index: number }[]): Promise<ServiceResponse<boolean>> => {
  if (!isSupabaseAvailable()) {
    return errorResponse(handleServiceError(new Error('Supabase not configured'), 'impactStatsService.reorderStats'));
  }

  try {
    // Update each stat's order_index
    const updates = stats.map(({ id, order_index }) =>
      safeFrom('impact_stats')
        .update({ order_index })
        .eq('id', id)
    );

    const results = await Promise.all(updates);
    const errors = results.filter(r => r.error);

    if (errors.length > 0) {
      console.error('Error reordering stats:', errors);
      return errorResponse(handleServiceError(new Error('Failed to reorder some stats'), 'impactStatsService.reorderStats'));
    }

    return successResponse(true);
  } catch (error) {
    return errorResponse(handleServiceError(error, 'impactStatsService.reorderStats'));
  }
};

